import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Select, Tag } from 'antd';

import { searchEdicoes, type EdicaoAdmin } from '../../../services/admin';
import { accessTag, isEdicaoFree, priceCentsOf } from './edicao-access';

const DEBOUNCE_MS = 300;

export interface EdicaoSelectProps {
  value?: number;
  /** Nome da edição já escolhida, para exibir quando ela não está entre os resultados. */
  valueLabel?: string | null;
  onChange: (id?: number, edicao?: EdicaoAdmin) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  allowClear?: boolean;
  /** Esconde das opções as edições que não passam no filtro. */
  filter?: (e: EdicaoAdmin) => boolean;
  /** Mostra à direita de cada linha se a edição é paga ou gratuita, com o valor. */
  showAccess?: boolean;
}

/** "2019-03-15T00:00:00.000Z" → "15/03/2019" (sem passar por Date, para não deslocar o dia). */
const formatDate = (iso: string | null): string => {
  const match = iso ? /^(\d{4})-(\d{2})-(\d{2})/.exec(iso) : null;
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '';
};

const AccessTag: React.FC<{ edicao: EdicaoAdmin }> = ({ edicao }) => {
  const tag = accessTag(isEdicaoFree(edicao), priceCentsOf(edicao));
  return (
    <Tag color={tag.color} style={{ marginRight: 0 }}>
      {tag.label}
    </Tag>
  );
};

export const EdicaoSelect: React.FC<EdicaoSelectProps> = ({
  value,
  valueLabel,
  onChange,
  placeholder = 'Edição',
  style,
  allowClear = true,
  filter,
  showAccess = false,
}) => {
  const [edicoes, setEdicoes] = useState<EdicaoAdmin[]>([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const requestId = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const load = useCallback((text: string) => {
    const id = ++requestId.current;
    const params = text ? { q: text, limit: 20 } : { limit: 10 };
    setLoading(true);
    searchEdicoes(params)
      .then((result) => {
        if (mounted.current && id === requestId.current) setEdicoes(result);
      })
      .catch(() => {
        if (mounted.current && id === requestId.current) setEdicoes([]);
      })
      .finally(() => {
        if (mounted.current && id === requestId.current) setLoading(false);
      });
  }, []);

  const handleSearch = (text: string) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => load(text.trim()), DEBOUNCE_MS);
  };

  const options = (filter ? edicoes.filter(filter) : edicoes).map((e) => ({
    value: e.id,
    label: (
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {e.nome ?? `Edição ${e.id}`}
          {e.dataPublicacao ? <small style={{ marginLeft: 8, opacity: 0.6 }}>{formatDate(e.dataPublicacao)}</small> : null}
        </span>
        {showAccess ? <AccessTag edicao={e} /> : null}
      </span>
    ),
  }));
  // Valor escolhido fora dos resultados (ex.: jogo antigo): mostra o nome, não o número cru.
  if (value !== undefined && !edicoes.some((e) => e.id === value)) {
    options.unshift({ value, label: <span>{valueLabel ?? `Edição ${value}`}</span> });
  }

  return (
    <Select
      showSearch
      filterOption={false}
      allowClear={allowClear}
      placeholder={placeholder}
      style={style}
      loading={loading}
      value={value}
      options={options}
      notFoundContent={loading ? 'Buscando…' : 'Nenhuma edição'}
      onDropdownVisibleChange={(open) => {
        if (open) load('');
      }}
      onSearch={handleSearch}
      onChange={(id?: number) => onChange(id, edicoes.find((e) => e.id === id))}
    />
  );
};
